import React, { useEffect, useState } from "react";
import { api, formatCurrency, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Megaphone, Plus, Trash2, AlertTriangle, Info, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const SEV = {
  info: { icon: Info, cls: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300", border: "border-blue-200 dark:border-blue-500/30" },
  warning: { icon: AlertTriangle, cls: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300", border: "border-amber-200 dark:border-amber-500/30" },
  critical: { icon: ShieldAlert, cls: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300", border: "border-red-200 dark:border-red-500/30" },
};

export default function SuperAdminAnnouncements() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/superadmin/announcements").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">Annonces</h1>
          <p className="text-sm text-slate-500">Messages diffusés à toutes les agences de la plateforme</p>
        </div>
        <NewAnnouncementDialog onCreated={load} />
      </div>

      <div className="space-y-3">
        {items.map((a) => {
          const s = SEV[a.severity] || SEV.info;
          const Icon = s.icon;
          return (
            <div key={a.id} data-testid={`sa-annonce-${a.id}`} className={`p-4 rounded-2xl border ${s.border} flex items-start gap-3 ${a.active ? "opacity-100" : "opacity-50"}`}>
              <div className={`w-9 h-9 rounded-lg ${s.cls} grid place-items-center shrink-0`}><Icon className="w-4 h-4" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-semibold">{a.title}</div>
                  {!a.active && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800">Inactive</span>}
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400 mt-1 whitespace-pre-wrap">{a.message}</div>
                <div className="text-xs text-slate-400 mt-2">{new Date(a.created_at).toLocaleString("fr-FR")}</div>
              </div>
              <Button variant="ghost" size="icon" onClick={async () => {
                if (!confirm("Supprimer cette annonce ?")) return;
                try { await api.delete(`/superadmin/announcements/${a.id}`); toast.success("Supprimée"); load(); }
                catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
              }} data-testid={`sa-annonce-delete-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="text-center py-16 text-slate-500">
            <Megaphone className="w-10 h-10 mx-auto mb-3 opacity-40" />
            Aucune annonce active. Créez-en une pour informer toutes les agences.
          </div>
        )}
      </div>
    </div>
  );
}

function NewAnnouncementDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", message: "", severity: "info", active: true });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="btn-pill bg-slate-900 hover:bg-slate-800 text-white" data-testid="sa-annonce-add-btn"><Plus className="w-4 h-4 mr-1" /> Nouvelle annonce</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Nouvelle annonce plateforme</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="sa-annonce-title" /></div>
          <div><Label>Message</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="sa-annonce-message" rows={4} /></div>
          <div><Label>Sévérité</Label>
            <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
              <SelectTrigger data-testid="sa-annonce-severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="info">ℹ️ Information</SelectItem>
                <SelectItem value="warning">⚠️ Avertissement</SelectItem>
                <SelectItem value="critical">🚨 Critique</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between"><span className="text-sm">Active</span><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /></div>
          <Button className="btn-pill bg-slate-900 hover:bg-slate-800 text-white w-full" data-testid="sa-annonce-save" onClick={async () => {
            if (!form.title || !form.message) { toast.error("Titre et message requis"); return; }
            try { await api.post("/superadmin/announcements", form); toast.success("Annonce publiée"); setOpen(false); setForm({ title: "", message: "", severity: "info", active: true }); onCreated(); }
            catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
          }}>Publier</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
